// Subscription helpers + React context. Device-scoped, restorable via parent login.
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authHeaders, getToken, useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = "ll_device_id";
const SUB_KEY = "ll_subscription";
const FREE = { plan: "free", status: "free", ends_at: null };

export function deviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) { id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`); localStorage.setItem(DEVICE_KEY, id); }
  return id;
}

export function cachedSubscription() {
  try { return JSON.parse(localStorage.getItem(SUB_KEY)) || { ...FREE }; } catch { return { ...FREE }; }
}
function cache(sub) { localStorage.setItem(SUB_KEY, JSON.stringify(sub)); return sub; }

export async function fetchPlans() {
  const r = await fetch(`${API}/api/plans`);
  if (!r.ok) throw new Error("Could not load plans");
  return r.json();
}

export async function fetchSubscription() {
  const r = await fetch(`${API}/api/subscription/${deviceId()}`);
  if (!r.ok) throw new Error("Could not load subscription");
  return cache(await r.json());
}

// Bind the logged-in parent's active plan to this device (cross-device restore).
async function restoreForUser() {
  const r = await fetch(`${API}/api/subscription/restore`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ device_id: deviceId() }),
  });
  if (!r.ok) return null;
  return r.json();
}

export async function fetchReceipts() {
  const url = getToken() ? `${API}/api/receipts/by-user/me` : `${API}/api/receipts/${deviceId()}`;
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) return [];
  return (await r.json()).receipts || [];
}

export function chooseFree() { return cache({ ...FREE }); }

function loadCheckout() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve; s.onerror = () => reject(new Error("Could not load Razorpay"));
    document.body.appendChild(s);
  });
}

/* Opens Razorpay Checkout. Resolves with the verified subscription (incl. receipt), or null if closed. */
export async function subscribe(planId, profileName) {
  const r = await fetch(`${API}/api/payments/create-order`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ plan: planId, device_id: deviceId() }),
  });
  if (!r.ok) throw new Error((await r.json()).detail || "Could not start payment");
  const order = await r.json();
  await loadCheckout();
  return new Promise((resolve, reject) => {
    const rz = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
      name: "Little Learner", description: `${order.plan.name} plan`, theme: { color: "#8B5CF6" },
      notes: { child: profileName || "" },
      config: {
        display: {
          blocks: { upi: { name: "Pay via UPI", instruments: [{ method: "upi" }] } },
          sequence: ["block.upi", "other"],
          preferences: { show_default_blocks: true },
        },
      },
      handler: async (res) => {
        try {
          const v = await fetch(`${API}/api/payments/verify`, {
            method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
            body: JSON.stringify({ ...res, device_id: deviceId() }),
          });
          if (!v.ok) throw new Error((await v.json()).detail || "Verification failed");
          resolve(cache(await v.json()));
        } catch (e) { reject(e); }
      },
      modal: { ondismiss: () => resolve(null) },
    });
    rz.on("payment.failed", (e) => reject(new Error(e.error?.description || "Payment failed")));
    rz.open();
  });
}

/* ---- React context so gating stays in sync across pages ---- */
const SubCtx = createContext(null);
export const useSub = () => useContext(SubCtx);

export function SubProvider({ children }) {
  const { user, ready } = useAuth();
  const [sub, setSubState] = useState(cachedSubscription());

  const setSub = useCallback((s) => { cache(s); setSubState(s); }, []);

  const refresh = useCallback(async () => {
    try {
      if (getToken()) {
        // Logged in: entitlement is scoped to the parent's email (restore also claims/binds this device).
        const restored = await restoreForUser();
        const s = restored && restored.status === "active"
          ? { plan: restored.plan, status: "active", ends_at: restored.ends_at, started_at: restored.started_at }
          : { ...FREE };
        setSub(s);
        return s;
      }
      // Logged out: only an anonymous (unclaimed) purchase on this device stays active.
      const s = await fetchSubscription();
      setSubState(s);
      return s;
    } catch { const c = cachedSubscription(); setSubState(c); return c; }
  }, [setSub]);

  useEffect(() => { if (ready) refresh(); }, [ready, user, refresh]);

  const premium = sub?.status === "active";
  return <SubCtx.Provider value={{ sub, premium, setSub, refresh }}>{children}</SubCtx.Provider>;
}
